import {Alert, HPButton, HPTextField} from '@hconnect/uikit/src/lib2'
import {useUpmPlantNow, useUpmPlantContext} from '@hconnect/upm'
import {FeatureFlagKey, useCommonFeatureFlag} from '@hproduce/feature-flags'
import {dataTestId} from '@hproduce/suite-core'
import {Memory, Close, Replay} from '@mui/icons-material'
import {Stack} from '@mui/material'
import {useMemo, useCallback} from 'react'
import {useForm, Controller} from 'react-hook-form'
import {useTranslation} from 'react-i18next'

import {FEATURE_NAMESPACE} from '../../consts'
import {useOptimizerSettings, useRecalculatePlan} from '../../hooks'
import {useDialog} from '../../providers'
import {OptimizerForecastType, type RecalculatePlanData} from '../../types'
import {requiredValidator} from '../../utils'
import {validateOptimizeFromDateTime} from '../optimizerSettings/optimizerPriorities.helpers'
import {OptimizerPrioritiesLegend} from '../optimizerSettings/OptimizerPrioritiesLegend'
import {OptimizerPrioritiesSlidersInput} from '../optimizerSettings/OptimizerPrioritiesSlidersInput'
import {OptimizerPrioritiesTriangleInput} from '../optimizerSettings/OptimizerPrioritiesTriangleInput'
import {OptimizerSettingsDateTimePicker} from '../optimizerSettings/OptimizerSettingsDateTimePicker'
import {OptimizerSettingsHorizonSlider} from '../optimizerSettings/OptimizerSettingsHorizonSlider'
import {OptimizerSettingsTimeoutInput} from '../optimizerSettings/OptimizerSettingsTimeoutInput'
import {PlannerCheckbox} from '../PlannerCheckbox'

export const RecalculatePlanDialogContent = () => {
  const {t} = useTranslation(FEATURE_NAMESPACE)
  const {plantId} = useUpmPlantContext()
  const shouldShowTimeframeSettings = useCommonFeatureFlag(FeatureFlagKey.OptimizerStatusDisplay, {
    plantId
  })

  const getPlantNow = useUpmPlantNow()

  const {data: optimizerSettings} = useOptimizerSettings()
  if (!optimizerSettings) {
    throw new Error('BUG: Optimizer settings should be loaded before opening the dialog')
  }

  const {recalculatePlan, isLoading} = useRecalculatePlan()

  const {
    control,
    formState: {isValid},
    reset,
    handleSubmit,
    watch
  } = useForm<RecalculatePlanData>({
    defaultValues: {
      optimizerSettings,
      saveSettings: false,
      name: '',
      forecastType: OptimizerForecastType.StatisticalForecast
    },
    mode: 'onChange'
  })

  const currentOptimizeFrom = watch('optimizerSettings.optimizeFrom')
  const currentHorizonDays = watch('optimizerSettings.horizonDays')
  const currentExpectedDuration = optimizerSettings.expectedDurations[currentHorizonDays.toString()]

  const {closeDialog} = useDialog()

  const submit = useMemo(() => handleSubmit(recalculatePlan), [handleSubmit, recalculatePlan])

  const handleRecalculate = useCallback(() => {
    void submit()
    closeDialog()
  }, [closeDialog, submit])

  const handleResetSettings = useCallback(() => reset(), [reset])

  const shouldDisableRecalculateButton = isLoading || !isValid

  return (
    <Stack spacing={2.75} sx={{maxWidth: ({spacing}) => spacing(90)}}>
      <Alert
        accentColor="info"
        content={t('optimizer.optimizerSettingsInfo')}
        {...dataTestId('optimizer_settings_info')}
      />

      {/* Triangle priorities */}
      <Stack direction="row" spacing={2} sx={{width: 1}}>
        <Controller
          name="optimizerSettings.priorities"
          control={control}
          render={({field: {value, onChange}}) => (
            <OptimizerPrioritiesTriangleInput
              initialValue={optimizerSettings.priorities}
              value={value}
              setValue={onChange}
            />
          )}
        />
        <Stack spacing={2} sx={{alignSelf: 'center', flex: 1}}>
          <OptimizerPrioritiesLegend />
          <Controller
            name="name"
            control={control}
            render={({field: {value, onChange}}) => (
              <HPTextField
                {...dataTestId('name_input')}
                label={t('optimizer.planName')}
                placeholder={t('optimizer.recalculatedPlan')}
                value={value}
                onChange={onChange}
                InputLabelProps={{shrink: true}}
              />
            )}
          />
          <Controller
            name="optimizerSettings.priorities"
            control={control}
            render={({field: {value, onChange}}) => {
              return <OptimizerPrioritiesSlidersInput value={value} setValue={onChange} />
            }}
          />
        </Stack>
      </Stack>

      {/* Timeframe settings */}
      {shouldShowTimeframeSettings && (
        <Stack spacing={1.5}>
          <Controller
            name="optimizerSettings.horizonDays"
            control={control}
            rules={{...requiredValidator(t)}}
            render={({field: {value, onChange}}) => (
              <OptimizerSettingsHorizonSlider
                optimizerSettings={optimizerSettings}
                value={value}
                setValue={onChange}
              />
            )}
          />
          <Stack direction="row" spacing={1.5}>
            <Controller
              name="optimizerSettings.timeLimit"
              control={control}
              rules={{...requiredValidator(t)}}
              render={({field: {value, onChange}}) => (
                <OptimizerSettingsTimeoutInput
                  errorMessage={
                    value < currentExpectedDuration.max ? t('optimizer.timeoutWarning') : undefined
                  }
                  value={value}
                  setValue={onChange}
                />
              )}
            />
            <Controller
              name="optimizerSettings.optimizeFrom"
              control={control}
              rules={{
                ...requiredValidator(t),
                validate: validateOptimizeFromDateTime(t, getPlantNow())
              }}
              render={({field: {value, onChange}, fieldState: {error}}) => (
                <OptimizerSettingsDateTimePicker
                  label={t('optimizer.optimizeFrom')}
                  errorMessage={error && error.message}
                  value={value}
                  setValue={onChange}
                  data-test-id="optimize-from-datetime-picker"
                />
              )}
            />
            <OptimizerSettingsDateTimePicker
              key={currentHorizonDays} // force re-mount when horizon changes
              label={t('optimizer.optimizeUntil')}
              value={currentOptimizeFrom.clone().add(currentHorizonDays, 'day')}
              data-test-id="optimize-until-datetime-picker"
            />
          </Stack>
        </Stack>
      )}

      {/* Save current settings */}
      <Controller
        name="saveSettings"
        control={control}
        render={({field: {value, onChange}}) => (
          <PlannerCheckbox
            sx={{width: 'fit-content'}}
            label={t('optimizer.saveAsPermanentSetting')}
            {...dataTestId('save_settings_checkbox')}
            value={value}
            onChange={onChange}
          />
        )}
      />

      {/* Action buttons */}
      <Stack spacing={1.5} direction="row" justifyContent="space-between" sx={{width: 1}}>
        <HPButton
          {...dataTestId('reset_button')}
          backgroundMode="light"
          variant="text"
          startIcon={<Replay />}
          onClick={handleResetSettings}
          sx={{alignSelf: 'flex-start'}}
        >
          {t('optimizer.resetToDefault')}
        </HPButton>
        <Stack direction="row" spacing={1.5}>
          <HPButton
            {...dataTestId('cancel_button')}
            backgroundMode="light"
            variant="text"
            startIcon={<Close />}
            onClick={closeDialog}
          >
            {t('common.cancel')}
          </HPButton>
          <HPButton
            {...dataTestId('recalculate_button')}
            variant="contained"
            backgroundMode="light"
            startIcon={<Memory />}
            onClick={handleRecalculate}
            disabled={shouldDisableRecalculateButton}
          >
            {t('optimizer.recalculate')}
          </HPButton>
        </Stack>
      </Stack>
    </Stack>
  )
}
